import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../api/client";
import { useAuth } from "./auth";

const MetaCtx = createContext(null);

// Departments + users + projects are needed by almost every screen (pickers,
// labels, filters). Loading them once here — instead of in each page's useMeta
// on every navigation — removes the 3 redundant round-trips that made tab
// switches feel slow. Cached for the session; refreshable on demand.
export function MetaProvider({ children }) {
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  // Departamentos que el usuario puede ELEGIR al crear/asignar (su depto +
  // anidados; todos para directivo/CFO/admin). Para los selectores de alta.
  const [misDepartamentos, setMisDepartamentos] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const startedRef = useRef(false);

  const loadProjects = useCallback(() => {
    return api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const loadAll = useCallback(() => {
    return Promise.all([
      api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {}),
      api.get("/departments/asignables").then((r) => setMisDepartamentos(r.data)).catch(() => {}),
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {}),
      loadProjects(),
    ]).finally(() => setLoaded(true));
  }, [loadProjects]);

  // Load once when a user session exists; reset the guard on logout.
  useEffect(() => {
    if (user && !startedRef.current) {
      startedRef.current = true;
      loadAll();
    } else if (!user) {
      startedRef.current = false;
      setLoaded(false);
      setDepartments([]); setMisDepartamentos([]); setUsers([]); setProjects([]);
    }
  }, [user, loadAll]);

  return (
    <MetaCtx.Provider value={{ departments, misDepartamentos, users, projects, loaded, reloadProjects: loadProjects, reloadMeta: loadAll }}>
      {children}
    </MetaCtx.Provider>
  );
}

export function useMeta() {
  const ctx = useContext(MetaCtx);
  // Fallback for any component rendered outside the provider (shouldn't happen).
  return ctx || { departments: [], misDepartamentos: [], users: [], projects: [], loaded: false, reloadProjects: () => {}, reloadMeta: () => {} };
}
